import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Briefcase, IndianRupee, Layers, CheckCircle, Globe, MonitorPlay, Code, Server, Database } from "lucide-react";

// --- SEO SCHEMA MARKUP (JSON-LD) ---
const courseSchema = {
  "@context": "https://schema.org",
  "@type": "Course",
  "name": "Online Python Full Stack Developer Training",
  "description": "Master Python, Django, Flask, React, and Data Science concepts from home. 100% Placement Assistance. Best Online Course for Full Stack Development and Software Engineering.",
  "provider": {
    "@type": "Organization",
    "name": "PIT Institute",
    "sameAs": "https://www.projectinfotech.in"
  },
  "coursePrerequisites": "Basic understanding of programming",
  "educationalCredentialAwarded": "Professional Certification in Python Full Stack Web Development",
  "hasCourseInstance": {
    "@type": "CourseInstance",
    "courseMode": "Online", 
    "location": "Virtual / Online",
    "courseWorkload": "PT10W" // 10 Weeks
  },
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "4.9",
    "reviewCount": "1850"
  }
};

// --- Helper Components ---

const StarRating = ({ rating }: { rating: number }) => {
  return (
    <div className="flex items-center" aria-label={`Rated ${rating} out of 5 stars`}>
      {[...Array(5)].map((_, i) => (
        <svg
          key={i}
          className={`h-4 w-4 ${i < rating ? "text-yellow-500" : "text-gray-300"} inline-block`}
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.538 1.118l-2.8-2.034a1 1 0 00-1.176 0l-2.8 2.034c-.783.57-1.838-.197-1.538-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.381-1.81.588-1.81h3.462a1 1 0 00.95-.69l1.07-3.292z" />
        </svg>
      ))}
    </div>
  );
};

const VerifiedBadge = () => (
  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 ml-2 border border-gray-300">
    <CheckCircle className="w-3 h-3 mr-1 text-green-600" /> Verified Student
  </span>
);

// --- Tools Component ---
const ToolsYouWillMaster = () => {
  const tools = [
    { name: 'Python', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/python/python-original.svg' },
    { name: 'Django', logo: 'https://images.seeklogo.com/logo-png/27/2/django-logo-png_seeklogo-273036.png?v=1957671457205160584' },
    { name: 'Flask', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/flask/flask-original.svg' },
    { name: 'PostgreSQL', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/postgresql/postgresql-original-wordmark.svg' },
    { name: 'React', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/react/react-original.svg' },
    { name: 'Docker', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/docker/docker-original.svg' },
    { name: 'Git', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/git/git-original.svg' },
    { name: 'NumPy', logo: 'https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/numpy/numpy-original-wordmark.svg' },
  ];

  return (
    // ID Added for Scroll Target
    <section id="Industry-Standard" className="py-16 md:py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
        <h2 className="text-3xl md:text-4xl font-bold mb-10 text-gray-900 font-aligran">
          Industry-Standard <span className="text-red-600">Full Stack Tools</span> You'll Master
        </h2>

        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-8 gap-6 md:gap-8 mb-16">
          {tools.map((tool, i) => (
            <motion.div
              key={i}
              className="flex flex-col items-center justify-center p-4 bg-white rounded-xl shadow-sm hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
            >
              <img src={tool.logo} alt={`${tool.name} for Python Full Stack Development`} className="w-12 h-12 md:w-16 md:h-16 mb-3 object-contain" />
              <p className="text-xs md:text-sm font-semibold text-gray-800">{tool.name}</p>
            </motion.div>
          ))}
        </div>

        <div className="grid md:grid-cols-2 gap-8 text-left">
           {/* FRONT-END FRAMEWORKS */}
           <div className="p-8 bg-white border border-gray-200 rounded-xl shadow-md hover:shadow-lg transition-shadow">
            <h3 className="text-2xl font-bold mb-4 text-black border-b pb-2 border-red-600/50 flex items-center gap-2">
              <MonitorPlay className="text-red-600"/> FRONT-END FRAMEWORKS
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Master the client side to build dynamic, fast, and responsive user interfaces that seamlessly communicate with the powerful Python backend APIs.
            </p>
            <ul className="list-disc list-inside space-y-1 text-black font-medium">
              <li>HTML5, CSS3, JavaScript (ES6+)</li>
              <li>React JS / Angular Development</li>
              <li>State Management (Redux/Context API)</li>
              <li>UI Libraries (Tailwind CSS, Bootstrap)</li>
            </ul>
          </div>

          {/* BACK-END FRAMEWORKS */}
          <div className="p-8 bg-white border border-gray-200 rounded-xl shadow-md hover:shadow-lg transition-shadow">
            <h3 className="text-2xl font-bold mb-4 text-black border-b pb-2 border-red-600/50 flex items-center gap-2">
              <Server className="text-red-600"/> BACK-END FRAMEWORKS
            </h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              Develop robust, scalable, and secure server-side software logic, microservices, and AI integrations using the powerful Python ecosystem.
            </p>
            <ul className="list-disc list-inside space-y-1 text-black font-medium">
              <li>Core Python Programming & OOPs</li>
              <li>Django Web Framework & REST APIs</li>
              <li>Flask Microservices</li>
              <li>PostgreSQL, MongoDB & Data Science basics</li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};


const PythonFullStack = () => {
  
  // --- UPDATED DATA WITH INDIAN STUDENT IMAGES ---
  const testimonials = [
    { 
      id: 1, 
      name: "Akash Verma", 
      review: "The best online platform for Python! I learnt Django and Flask thoroughly and got placed in a top tech company as a Full Stack Engineer.", 
      rating: 5, 
      date: "2024-07-30", 
      profileImg: "https://images.unsplash.com/photo-1633332755192-727a05c4013d?w=400&h=400&fit=crop", 
      source: "Google Reviews" 
    },
    { 
      id: 2, 
      name: "Sneha Reddy", 
      review: "I came with no coding background and now I can confidently build web apps with Python. The online live tech projects were amazing.", 
      rating: 4, 
      date: "2024-07-28", 
      profileImg: "https://images.unsplash.com/photo-1628157588553-5eeea00af15c?w=400&h=400&fit=crop", 
      source: "LinkedIn" 
    },
    { 
      id: 3, 
      name: "Vikas Taneja", 
      review: "From Core Python to advanced Django REST API, the curriculum covers everything needed for web development. Excellent IT placement assistance globally!", 
      rating: 5, 
      date: "2024-07-25", 
      profileImg: "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=400&h=400&fit=crop", 
      source: "Website Feedback" 
    },
    { 
      id: 4, 
      name: "Divya Das", 
      review: "The emphasis on practical application made this course stand out. I loved learning about Data Science basics along with React and Python from home.", 
      rating: 5, 
      date: "2024-07-23", 
      profileImg: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&h=400&fit=crop", 
      source: "Google Reviews" 
    },
    { 
      id: 5, 
      name: "Rohan Patil", 
      review: "Hands down the most comprehensive Online Python Full Stack training. Got placed as a software developer within two months after finishing!", 
      rating: 4, 
      date: "2024-07-20", 
      profileImg: "https://images.unsplash.com/photo-1599566150163-29194dcaad36?w=400&h=400&fit=crop", 
      source: "LinkedIn" 
    },
  ];

  const faqContent = [
    { title: "What is Python Full Stack Web Development?", answer: "Python Full Stack Development involves mastering both front-end (HTML, CSS, React/Angular) and back-end (Python, Django, Flask, Databases) software technologies to build complete web applications." },
    { title: "Is this online coding course suitable for beginners?", answer: "Yes, our curriculum starts from zero (Core Python) and scales up to advanced Full Stack Web Development and API integration, making it perfect for freshers." },
    { title: "Why choose Python Full Stack over Java or MERN?", answer: "Python is incredibly versatile and dominates the fields of AI, Data Science, and Machine Learning. Choosing Python Full Stack gives you a backend that easily integrates with AI, making it a great alternative to the MERN stack or Java Full Stack." },
    { title: "Do you provide IT placement assistance?", answer: "Absolutely. We have strong tie-ups with 500+ tech companies globally, ensuring guaranteed interview opportunities for our developer candidates." },
    { title: "How are the programming classes conducted?", answer: "We offer interactive Instructor-led Live Online Classes. You also get lifetime access to recorded coding sessions on our learning portal." },
  ];

  const curriculumContent = [
    { title: "Module 1: Core Python Programming", description: "Fundamentals, Data Structures (Lists, Dicts), OOPs concepts, File Handling, and basic I/O operations for backend logic." },
    { title: "Module 2: Advanced Python & Libraries", description: "Regular Expressions, Multithreading, NumPy for scientific computing, and Pandas for data manipulation." },
    { title: "Module 3: Database Design (SQL & PostgreSQL)", description: "SQL commands, Complex Joins, Subqueries, Stored Procedures, and connecting Python web applications to PostgreSQL/MySQL." },
    { title: "Module 4: Django Web Framework (MVT)", description: "Model-View-Template architecture, Django ORM, Admin Interface, and building secure CRUD operations." },
    { title: "Module 5: Flask Micro Framework", description: "Flask routing, Jinja2 templating, configuring extensions, and building lightweight Python web applications." },
    { title: "Module 6: Django REST Framework (DRF)", description: "Building secure, highly scalable RESTful APIs using DRF for mobile apps and React frontend consumption." },
    { title: "Module 7: Frontend UI Integration (React)", description: "HTML5, CSS3, JavaScript basics, and seamlessly integrating a React JS frontend with the Python Django/Flask backend via APIs." },
    { title: "Module 8: DevOps & Cloud Deployment", description: "Virtual Environments, Version Control (Git), Docker basics, and deploying Python applications on Cloud servers (AWS/Heroku)." },
    { title: "Module 9: Full Stack Capstone Project", description: "Develop and deploy a complete social media platform, SaaS app, or e-commerce website using the Python Full Stack." },
  ];

  const highlights = [
    { title: 'Live Full Stack Projects', desc: 'Work on real-world e-commerce & banking web apps.', img: 'https://images.unsplash.com/photo-1519389950473-47ba0277781c' },
    { title: 'Expert Tech Mentors', desc: 'Learn directly from Python developers working in top MNCs.', img: 'https://images.unsplash.com/photo-1531482615713-2afd69097998' },
    { title: 'Global IT Certification', desc: 'Get an ISO-recognized software course completion certificate.', img: 'https://images.unsplash.com/photo-1516321497487-e288fb19713f' },
    { title: '100% Placement Support', desc: 'Resume building, mock tech interviews & job referrals.', img: 'https://images.unsplash.com/photo-1603575448878-868a20723f5d' },
    { title: 'Live Online Coding Classes', desc: 'Interactive sessions with lifetime recording access.', img: 'https://cdni.iconscout.com/illustration/premium/thumb/hybrid-learning-8088108-6556132.png' },
    { title: 'Modern Tool Mastery', desc: 'Master Git, Docker, Pip, Django APIs, and IDEs.', img: 'https://static.vecteezy.com/system/resources/previews/018/769/685/original/industrial-revolution-4-0-sci-fi-hologram-and-3d-numbers-with-conceptual-icons-industry-4-0-concept-intelligence-robotics-physical-systems-illustrator-vector.jpg' },
    { title: 'Comprehensive Modules', desc: 'Deep dive into every frontend and backend module.', img: 'https://images.unsplash.com/photo-1521737604893-d14cc237f11d' },
    { title: 'Industry-Ready Syllabus', desc: 'Curriculum updated as per the latest IT and AI standards.', img: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0' },
    { title: '24/7 Developer Support', desc: 'Instant code debugging and doubt support via Chat/Call.', img: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f' },
  ];

  // Logic
  const [currentTestimonialIndex, setCurrentTestimonialIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(1);
  const [formData, setFormData] = useState({ firstName: "", businessEmail: "", phone: "" });

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) setVisibleCount(3);
      else if (window.innerWidth >= 768) setVisibleCount(2);
      else setVisibleCount(1);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonialIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  const currentVisibleTestimonials = [];
  for (let i = 0; i < visibleCount; i++) {
    currentVisibleTestimonials.push(testimonials[(currentTestimonialIndex + i) % testimonials.length]);
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    alert("Thank you! Our counselor will contact you shortly regarding the Python Full Stack program.");
    setFormData({ firstName: "", businessEmail: "", phone: "" });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="bg-white text-gray-900 font-sans w-full overflow-x-hidden">
      
      {/* SEO META TAGS - OPTIMIZED FOR PYTHON */}
      <Helmet>
        <title>Best Online Python Full Stack Developer Course | Django & React | PIT Institute</title>
        <meta name="description" content="Join the ultimate Online Python Full Stack Training. Master Python, Django, Flask, and React JS. 100% IT Placement Assistance and Global Certification." />
        <meta name="keywords" content="Online Python Course, Python Full Stack Training, Full Stack Web Development, Java Full Stack Alternative, MERN Stack Alternative, Django Course, Flask Training, Software Developer Course" />
        <meta property="og:title" content="Become a Certified Python Full Stack Developer Online" />
        <meta property="og:description" content="Master Backend (Python, Django, Flask) and Frontend (React) development from anywhere. Get 100% Placement assistance with top MNCs." />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify(courseSchema)}</script>
      </Helmet>

      {/* --- HERO SECTION --- */}
      <header className="relative w-full min-h-[600px] flex items-center bg-black py-16 md:py-0 overflow-visible">
        
        {/* Background Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1555066931-4365d14bab8c?q=80&w=2070&auto=format&fit=crop"
            alt="Python Full Stack Web Developer Online Training Background"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/95 to-red-900/30" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 grid md:grid-cols-2 gap-12 items-center">
          
          {/* Left Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center md:text-left"
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 mb-6 border border-red-500/50 rounded-full bg-red-900/20 text-red-400 text-xs md:text-sm font-bold tracking-wider uppercase">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
              </span>
              #1 Rated Online Tech Learning Platform
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold leading-tight mb-6 text-white font-aligran">
              Master <span className="text-red-600">Python Full Stack</span> From Anywhere
            </h1>

            <p className="text-base sm:text-lg md:text-xl text-gray-300 mb-8 leading-relaxed max-w-xl mx-auto md:mx-0">
              Launch your software engineering career with the <strong>Best Online Python Training</strong>. Master Django APIs, Flask, React UI, and Cloud deployment with <strong>Global IT Placement Support</strong>.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
              <button
                onClick={() => document.getElementById("enquiry-form")?.scrollIntoView({ behavior: "smooth" })}
                className="bg-red-600 hover:bg-red-700 text-white font-bold py-4 px-8 rounded-xl shadow-lg shadow-red-600/30 transition transform hover:scale-105"
              >
                Start Learning Tech Free
              </button>
              
              <button 
                onClick={() => document.getElementById("Industry-Standard")?.scrollIntoView({ behavior: "smooth" })}
                className="bg-transparent border-2 border-white/20 hover:border-white text-white font-bold py-4 px-8 rounded-xl transition hover:bg-white/5"
              >
                View Full Curriculum
              </button>
            </div>
          </motion.div>

          {/* Right Image/Graphic with FIXED PYTHON LOGO */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="flex justify-center relative"
          >
            <div className="relative z-10 bg-gray-900/80 backdrop-blur-sm p-6 rounded-2xl border border-gray-700 shadow-2xl w-full max-w-md">
                <div className="flex items-center gap-4 mb-4 border-b border-gray-700 pb-4">
                    <div className="w-3 h-3 rounded-full bg-red-500"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                    <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    <span className="text-gray-400 text-xs font-mono ml-auto">OnlineClass.py</span>
                </div>
                <code className="text-sm md:text-base font-mono text-green-400 block overflow-x-auto">
                    <span className="text-purple-400">class</span> <span className="text-yellow-400">SoftwareDeveloper</span>:<br/>
                    &nbsp;&nbsp;<span className="text-purple-400">def</span> <span className="text-blue-400">__init__</span>(self):<br/>
                    &nbsp;&nbsp;&nbsp;&nbsp;print(<span className="text-orange-400">"Learning Full Stack!"</span>)<br/>
                    &nbsp;&nbsp;&nbsp;&nbsp;self.skills = [<span className="text-orange-400">"Python"</span>, <span className="text-orange-400">"Django"</span>]<br/>
                    &nbsp;&nbsp;&nbsp;&nbsp;self.goal = <span className="text-orange-400">"Remote Tech Job"</span>
                </code>
                
                {/* --- FIXED PYTHON LOGO POSITION --- */}
                <img 
                    src="https://www.pngall.com/wp-content/uploads/5/Python-PNG-HD-Image.png" 
                    alt="Python Programming Language Logo"
                    className="absolute -bottom-6 -right-6 md:-bottom-8 md:-right-8 w-32 h-32 md:w-44 md:h-44 drop-shadow-2xl animate-pulse duration-[3000ms] z-20 object-contain"
                />
            </div>
          </motion.div>

        </div>
      </header>

      {/* --- WHY CHOOSE US (ONLINE FOCUS) --- */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center max-w-3xl mx-auto mb-16">
                <h2 className="text-3xl md:text-5xl font-extrabold text-gray-900 mb-6 font-aligran">
                    Why Choose Our <span className="text-red-600">IT Certification?</span>
                </h2>
                <p className="text-lg text-gray-600">
                    We don't just teach syntax; we build software engineering careers. Our Online Python Full Stack Course is designed to make you the top choice for tech recruiters globally.
                </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
                {[
                    { icon: <Briefcase size={32}/>, title: "100% IT Job Guarantee", desc: "Our dedicated placement cell ensures you get developer interview opportunities until you are placed.", color: "bg-red-50 text-red-600" },
                    { icon: <IndianRupee size={32}/>, title: "High Tech Salary Packages", desc: "Our alumni start with software developer packages ranging from 5 LPA to 12 LPA.", color: "bg-green-50 text-green-600" },
                    { icon: <MonitorPlay size={32}/>, title: "Live Online Coding Classes", desc: "Interactive instructor-led tech sessions with recording access for lifetime revision.", color: "bg-blue-50 text-blue-600" }
                ].map((item, idx) => (
                    <div key={idx} className="p-8 rounded-2xl border border-gray-100 shadow-lg hover:shadow-xl transition-shadow bg-white text-center group">
                        <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 ${item.color} group-hover:scale-110 transition-transform`}>
                            {item.icon}
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                        <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
                    </div>
                ))}
            </div>
        </div>
      </section>

      {/* --- HIGHLIGHTS --- */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-gray-900 font-aligran">
                Course <span className="text-red-600">Highlights</span>
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {highlights.map((h, i) => (
                    <motion.div
                        key={i}
                        className="bg-white rounded-xl shadow-md hover:shadow-lg transition overflow-hidden flex h-40 border-l-4 border-red-600"
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: i * 0.05 }}
                    >
                        <div className="w-1/2 p-5 flex flex-col justify-center">
                            <h3 className="font-bold text-lg text-black mb-1 leading-tight">{h.title}</h3>
                            <p className="text-gray-600 text-xs md:text-sm leading-relaxed">{h.desc}</p>
                        </div>
                        <div className="w-1/2 relative">
                            <img src={h.img} alt={`${h.title} - Python Web Development`} className="h-full w-full object-cover" loading="lazy" />
                            <div className="absolute inset-0 bg-gradient-to-l from-black/20 to-transparent"></div>
                        </div>
                    </motion.div>
                ))}
            </div>
        </div>
      </section>

      {/* --- TOOLS SECTION --- */}
      <ToolsYouWillMaster />

      {/* --- CURRICULUM --- */}
      <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
              <h2 className="text-3xl md:text-4xl font-bold mb-10 text-center text-gray-900 font-aligran">
                  Detailed Software <span className="text-red-600">Syllabus</span>
              </h2>
              <div className="space-y-4">
                  {curriculumContent.map((mod, i) => (
                      <details key={i} className="group border border-gray-200 rounded-lg bg-gray-50 open:bg-white open:shadow-md transition">
                          <summary className="flex items-center justify-between p-4 md:p-5 font-bold text-gray-900 cursor-pointer text-base md:text-lg select-none">
                              <span className="flex items-center gap-3">
                                  <span className="bg-red-100 text-red-700 text-xs px-2 py-1 rounded uppercase tracking-wide">Module {i + 1}</span>
                                  {mod.title.split(":")[1]}
                              </span>
                              <span className="text-red-500 text-xl font-bold group-open:rotate-180 transition-transform">▼</span>
                          </summary>
                          <div className="px-5 pb-5 text-gray-600 text-sm md:text-base leading-relaxed border-t border-gray-100 pt-3 ml-12">
                              {mod.description}
                          </div>
                      </details>
                  ))}
              </div>
          </div>
      </section>

      {/* --- FAQ & ENQUIRY FORM --- */}
      <section id="enquiry-form" className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-3 gap-12">
              
              {/* FAQ Column */}
              <div className="lg:col-span-2">
                  <h2 className="text-3xl font-bold mb-8 text-gray-900 font-aligran">Developer <span className="text-red-600">FAQs</span></h2>
                  <div className="space-y-4">
                      {faqContent.map((faq, i) => (
                          <details key={i} className="group bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                              <summary className="flex justify-between items-center font-semibold text-gray-900 cursor-pointer list-none">
                                  {faq.title}
                                  <span className="text-red-600 text-2xl font-light group-open:hidden">+</span>
                                  <span className="text-red-600 text-2xl font-light hidden group-open:block">-</span>
                              </summary>
                              <p className="mt-4 text-gray-600 leading-relaxed text-sm">{faq.answer}</p>
                          </details>
                      ))}
                  </div>
              </div>

              {/* Form Column */}
              <div className="lg:col-span-1">
                  <div className="bg-white p-8 rounded-2xl shadow-xl border-t-4 border-red-600 sticky top-24">
                      <h3 className="text-2xl font-bold text-gray-900 text-center mb-2">Join Next Tech Batch</h3>
                      <p className="text-center text-gray-500 text-sm mb-6">Limited coding slots available globally!</p>
                      
                      <form onSubmit={handleSubmit} className="space-y-5">
                          <div>
                            <label className="text-xs font-bold text-gray-500 uppercase">Full Name</label>
                            <input 
                                type="text" 
                                name="firstName"
                                required
                                value={formData.firstName}
                                onChange={handleInputChange}
                                className="w-full mt-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition text-gray-900"
                                placeholder="Enter your name"
                            />
                          </div>
                          <div>
                            <label className="text-xs font-bold text-gray-500 uppercase">Email Address</label>
                            <input 
                                type="email" 
                                name="businessEmail"
                                required
                                value={formData.businessEmail}
                                onChange={handleInputChange}
                                className="w-full mt-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition text-gray-900"
                                placeholder="Enter your email"
                            />
                          </div>
                          <div>
                            <label className="text-xs font-bold text-gray-500 uppercase">Phone Number</label>
                            <input 
                                type="tel" 
                                name="phone"
                                required
                                value={formData.phone}
                                onChange={handleInputChange}
                                className="w-full mt-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition text-gray-900"
                                placeholder="Enter 10-digit number"
                            />
                          </div>
                          <button type="submit" className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-3.5 rounded-lg shadow-lg shadow-red-500/30 transition transform active:scale-95">
                              Reserve My Tech Spot
                          </button>
                      </form>
                      <div className="mt-6 flex items-center justify-center gap-2 text-xs text-gray-400">
                          <Globe size={14} /> Accessible Worldwide
                      </div>
                  </div>
              </div>
          </div>
      </section>

      {/* --- TESTIMONIALS --- */}
      <section className="py-20 bg-white border-t border-gray-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
              <h2 className="text-3xl md:text-4xl font-bold mb-12 text-gray-900 text-center font-aligran">
                  What Our Tech Alumni <span className="text-red-600">Say</span>
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <AnimatePresence mode="popLayout">
                      {currentVisibleTestimonials.map((t) => (
                          <motion.div
                              key={t.id}
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -20 }}
                              className="bg-gray-50 p-6 rounded-2xl border border-gray-200 hover:border-red-300 transition-colors shadow-sm flex flex-col"
                          >
                              <div className="flex items-center gap-4 mb-4">
                                  <img src={t.profileImg} alt={`${t.name} - Python Course Alumni`} className="w-12 h-12 rounded-full object-cover border-2 border-red-200" loading="lazy" />
                                  <div>
                                      <h4 className="font-bold text-gray-900 text-sm md:text-base">{t.name}</h4>
                                      <span className="text-xs text-red-500 font-semibold">{t.source}</span>
                                  </div>
                              </div>
                              <StarRating rating={t.rating} />
                              <p className="mt-3 text-gray-600 text-sm leading-relaxed italic">"{t.review}"</p>
                              <div className="mt-auto pt-4 text-right">
                                <VerifiedBadge />
                              </div>
                          </motion.div>
                      ))}
                  </AnimatePresence>
              </div>
              
              <div className="flex justify-center mt-8 space-x-2"> 
                {testimonials.map((_, idx) => (
                    <button
                        key={idx}
                        onClick={() => setCurrentTestimonialIndex(idx)}
                        aria-label={`View student review ${idx + 1}`}
                        className={`h-2 w-2 rounded-full transition-all ${idx === currentTestimonialIndex ? 'bg-red-600 w-6' : 'bg-gray-300'}`}
                    />
                ))}
              </div>
          </div>
      </section>

    </div>
  );
};

export default PythonFullStack;